/**
 * As queries de Administração — **API Adm**, e nada mais.
 *
 * 🔒 **Nada aqui é escopado por cliente, e por isso tudo usa `chaveGlobal`.**
 * Administração gerencia tenants, então está acima deles: não existe "os grupos
 * do Tribunal de Justiça", existem os grupos. Usar `chaveDeQuery` faria N cópias
 * idênticas no cache e N requisições onde uma basta — e sugeriria um vínculo
 * grupo × cliente que BR-18 nega.
 *
 * 🔒 Este arquivo vive em `api/queries/` e não em `modules/admin/` porque o lint
 * proíbe uma pasta de módulo de importar o cliente do outro gateway. A tela de
 * Administração importa daqui; a fronteira do §5.5 continua checada pelo build.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiAdm } from '@/api/clients/apiAdm'
import {
  clienteCriadoSchema,
  clienteSchema,
  conviteSchema,
  grupoSchema,
  usuarioSchema,
  type NovoCliente,
  type Permissao,
} from '@/api/contracts/administracao'
import { servicoAwsSchema, statusDoCatalogoSchema } from '@/api/contracts/catalogo'

import { chaveGlobal } from './chaveDeQuery'

// ── 2n — Clientes ───────────────────────────────────────────────────────────
export function useClientesAdm() {
  return useQuery({
    queryKey: chaveGlobal('adm-clientes'),
    queryFn: async () => z.array(clienteSchema).parse(await apiAdm.get<unknown>('/clientes')),
  })
}

/**
 * O toggle de módulo por tenant (BR-11).
 *
 * Endpoint por módulo, e não PATCH do cliente inteiro: ligar um módulo é uma
 * operação auditável por si, e um PATCH do agregado faria dois administradores
 * com a tela aberta sobrescreverem o trabalho um do outro.
 */
export function useAlternarModuloDoCliente() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idCliente: string; modulo: string; habilitado: boolean }) =>
      clienteSchema.parse(
        await apiAdm.put<unknown>(`/clientes/${params.idCliente}/modulos/${params.modulo}`, {
          habilitado: params.habilitado,
        }),
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-clientes') }),
  })
}

/**
 * `POST /clientes` — novo cliente (tenant).
 *
 * Invalidates only `adm-clientes`: the `2n` table and the client pickers of `2o`
 * read that key. **`/eu` does NOT need a refresh** — a new client is linked to no
 * user yet, and the global client selector lists only the clients on the signed-in
 * user's own list (§5.2). It shows up there once someone is linked to it in `2o`.
 */
export function useCriarCliente() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: NovoCliente) =>
      clienteCriadoSchema.parse(await apiAdm.post<unknown>('/clientes', dados)),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-clientes') }),
  })
}

// ── 2o — Usuários & convites ────────────────────────────────────────────────
export function useUsuariosAdm() {
  return useQuery({
    queryKey: chaveGlobal('adm-usuarios'),
    queryFn: async () => z.array(usuarioSchema).parse(await apiAdm.get<unknown>('/usuarios')),
  })
}

/**
 * Os convites, lidos SEPARADAMENTE dos usuários.
 *
 * ⚠️ A `2o` desenha uma coluna `CONVITE` na mesma linha do usuário, mas o
 * contrato §7.5 não põe status de convite no usuário — são dois agregados. A
 * tela junta os dois **por e-mail**, em vez de eu acrescentar um campo
 * `statusConvite` que o backend nunca declarou. As duas rotas já existiam; a
 * junção não inventa nada.
 */
export function useConvites() {
  return useQuery({
    queryKey: chaveGlobal('adm-convites'),
    queryFn: async () => z.array(conviteSchema).parse(await apiAdm.get<unknown>('/convites')),
  })
}

/**
 * 🔒 **Convidar É cadastrar**, e por isso `grupos` e `clientes` viajam aqui.
 *
 * Em 01/09/2026 três contas da iOX foram criadas sem vínculo nenhum: elas
 * autenticavam, viam os menus certos e tomavam 403 em TODA tela de FinOps,
 * porque a lista de clientes delas estava vazia. Convidar sem vincular reproduz
 * esse estado exato — então o backend grava as duas listas na MESMA transação do
 * convite, e o formulário as pede.
 *
 * 🔒 BR-18: são DUAS listas independentes, nunca um par grupo×cliente.
 */
export function useConvidarUsuario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: {
      nome: string
      email: string
      grupos: string[]
      clientes: string[]
    }) => conviteSchema.parse(await apiAdm.post<unknown>('/convites', dados)),
    onSuccess: () =>
      void Promise.all([
        client.invalidateQueries({ queryKey: chaveGlobal('adm-convites') }),
        // O usuário passa a existir no mesmo instante — sem isto a tabela ao
        // lado só mostraria o convidado depois de um refresh manual.
        client.invalidateQueries({ queryKey: chaveGlobal('adm-usuarios') }),
      ]),
  })
}

/**
 * A **segunda metade** do convite: criar a conta no Cognito e disparar o e-mail.
 *
 * ⚠️ **São duas chamadas de propósito, e não por desleixo.** `POST /convites` é
 * servido pela Lambda Gestão Administrativa, que roda DENTRO da VPC para
 * alcançar o Aurora — e Lambda em VPC perde a saída para a internet. Sem NAT
 * Gateway, ela só chegaria ao Cognito por um Interface Endpoint dedicado, que
 * custa ~US$46/mês. Esta rota é servida por uma Lambda irmã que roda FORA da
 * VPC, onde `cognito-idp` é um endpoint público e o custo é zero.
 *
 * Não há transação entre os dois passos — não existe commit em duas fases entre
 * Postgres e Cognito. O que existe é um estado intermediário HONESTO: a pessoa
 * fica cadastrada com as permissões dela e sem conta de acesso. É o estado em
 * que a plataforma viveu até 02/09/2026, a operação é idempotente
 * (`CONTA_JA_EXISTE` é 409, não erro), e a `2o` sabe mostrá-lo e reparar.
 */
export function useCriarContaDeAcesso() {
  return useMutation({
    mutationFn: async (dados: { nome: string; email: string }) =>
      apiAdm.post<unknown>('/cognito/contas', dados),
  })
}

/**
 * Reenviar o convite de quem ainda não entrou.
 *
 * ⚠️ **Existe porque o convidado pode ficar preso sem saída.** A senha temporária
 * do pool vale **7 dias**, e uma conta em `FORCE_CHANGE_PASSWORD` **não consegue**
 * usar "esqueci minha senha" — o Cognito recusa com `NotAuthorizedException`.
 * Passado o prazo, só um Administrador destrava, e até 02/09/2026 isso exigia
 * `admin-create-user --message-action RESEND` na linha de comando.
 *
 * Rota separada de `POST /cognito/contas` porque são chamadas diferentes ao
 * Cognito, que falham por motivos diferentes: criar bate em `UsernameExists`,
 * reenviar bate em `UnsupportedUserState` (quem já ativou).
 */
export function useReenviarConvite() {
  return useMutation({
    mutationFn: async (dados: { email: string }) =>
      apiAdm.post<unknown>('/cognito/contas/reenvios', dados),
  })
}

/**
 * 🔒 `clientes` e `grupos` viajam como DUAS listas independentes (BR-18).
 *
 * Não existe "o grupo do usuário naquele cliente": ele carrega uma lista de
 * grupos e uma de clientes, e a permissão efetiva é a união dos grupos aplicada
 * a qualquer cliente que ele alcance. É exatamente a consequência que o Eduardo
 * convidou a gente a questionar (§9) — e que esta assinatura torna visível em
 * vez de esconder atrás de um par.
 */
export function useSalvarVinculosDoUsuario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { chave: string; clientes: string[]; grupos: string[] }) =>
      usuarioSchema.parse(
        await apiAdm.put<unknown>(`/usuarios/${params.chave}`, {
          clientes: params.clientes,
          grupos: params.grupos,
        }),
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-usuarios') }),
  })
}

export function useExcluirUsuario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (chave: string) => apiAdm.delete<unknown>(`/usuarios/${chave}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-usuarios') }),
  })
}

// ── 2q — Perfis & permissões ────────────────────────────────────────────────
export function useGrupos() {
  return useQuery({
    queryKey: chaveGlobal('adm-grupos'),
    queryFn: async () => z.array(grupoSchema).parse(await apiAdm.get<unknown>('/grupos')),
  })
}

export function useSalvarPermissoesDoGrupo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { idGrupo: string; permissoes: Permissao[] }) =>
      grupoSchema.parse(
        await apiAdm.put<unknown>(`/grupos/${params.idGrupo}`, {
          permissoes: params.permissoes,
        }),
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-grupos') }),
  })
}

export function useCriarGrupo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (nome: string) =>
      grupoSchema.parse(await apiAdm.post<unknown>('/grupos', { nome })),
    onSuccess: () => void client.invalidateQueries({ queryKey: chaveGlobal('adm-grupos') }),
  })
}

/**
 * `DELETE /grupos/{id}` — **cascading**: the Lambda removes the grupo from every
 * user that had it, in the same transaction (decided by Fábio, 2026-09-17).
 *
 * That is why `adm-usuarios` is invalidated alongside `adm-grupos`: the `2o` table
 * lists grupos per user. The session's `/eu` is not a TanStack query (it lives in
 * `SessionProvider`), so refreshing the signed-in user's own permissions is the
 * screen's job — `Perfis` calls `recarregarPerfil()` when the user was in the grupo.
 */
export function useExcluirGrupo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (idGrupo: string) =>
      apiAdm.delete<unknown>(`/grupos/${encodeURIComponent(idGrupo)}`),
    onSuccess: () =>
      void Promise.all([
        client.invalidateQueries({ queryKey: chaveGlobal('adm-grupos') }),
        client.invalidateQueries({ queryKey: chaveGlobal('adm-usuarios') }),
      ]),
  })
}

// ── 2p — Configurações globais (BR-24) ──────────────────────────────────────
export function useCatalogoDeStatusAdm() {
  return useQuery({
    queryKey: chaveGlobal('adm-catalogo-status'),
    queryFn: async () =>
      z
        .object({ itens: z.array(statusDoCatalogoSchema) })
        .parse(await apiAdm.get<unknown>('/catalogos/status')),
  })
}

export function useCatalogoDeServicosAdm() {
  return useQuery({
    queryKey: chaveGlobal('adm-catalogo-servicos'),
    queryFn: async () =>
      z
        .object({ itens: z.array(servicoAwsSchema) })
        .parse(await apiAdm.get<unknown>('/catalogos/servicos')),
  })
}

/**
 * ⚠️ Invalida TAMBÉM as chaves de catálogo da API User.
 *
 * As telas de módulo leem o mesmo catálogo pelo outro gateway
 * (`catalogo-status`, `catalogo-servicos`). Sem esta invalidação, um
 * Administrador cria um status aqui e ele não aparece no dropdown da `2e` na
 * mesma sessão — e o bug pareceria do FinOps, não desta tela.
 */
function invalidarCatalogos(client: ReturnType<typeof useQueryClient>, quais: string[]) {
  return Promise.all(quais.map((c) => client.invalidateQueries({ queryKey: chaveGlobal(c) })))
}

export function useCriarStatus() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (rotulo: string) =>
      statusDoCatalogoSchema.parse(await apiAdm.post<unknown>('/catalogos/status', { rotulo })),
    onSuccess: () => void invalidarCatalogos(client, ['adm-catalogo-status', 'catalogo-status']),
  })
}

export function useExcluirStatus() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (idStatus: string) => apiAdm.delete<unknown>(`/catalogos/status/${idStatus}`),
    onSuccess: () => void invalidarCatalogos(client, ['adm-catalogo-status', 'catalogo-status']),
  })
}

export function useCriarServico() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (rotulo: string) =>
      servicoAwsSchema.parse(await apiAdm.post<unknown>('/catalogos/servicos', { rotulo })),
    onSuccess: () =>
      void invalidarCatalogos(client, ['adm-catalogo-servicos', 'catalogo-servicos']),
  })
}

export function useExcluirServico() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (idServico: string) =>
      apiAdm.delete<unknown>(`/catalogos/servicos/${encodeURIComponent(idServico)}`),
    onSuccess: () =>
      void invalidarCatalogos(client, ['adm-catalogo-servicos', 'catalogo-servicos']),
  })
}
